import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Inbox as InboxIcon, 
  AlertTriangle, 
  RefreshCw, 
  TrendingDown, 
  FileText,
  CheckCircle,
  Loader2,
  Sparkles,
  Bell,
  Play
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useAgentMemory } from '@/hooks/useAgentMemory';
import { SEO } from '@/components/SEO';
import { ActionCard, AlertSeverity, AlertStatus } from '@/components/Inbox/ActionCard';
import { EvidenceDrawer } from '@/components/Inbox/EvidenceDrawer';
import { AgentTimeline } from '@/components/AgentTimeline';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

interface Alert {
  id: string;
  alert_type: string;
  title: string;
  description: string | null;
  severity: AlertSeverity;
  status: AlertStatus;
  money_impact: number | null;
  evidence: any;
  action_required: boolean;
  related_entity_type: string | null;
  related_entity_id: string | null;
  created_at: string;
}

const alertTypeIcons: Record<string, React.ReactNode> = {
  duplicate_invoice: <AlertTriangle className="h-5 w-5" />,
  unmatched_transaction: <RefreshCw className="h-5 w-5" />,
  runway_warning: <TrendingDown className="h-5 w-5" />,
  anomaly: <AlertTriangle className="h-5 w-5" />,
  approval_required: <FileText className="h-5 w-5" />,
};

export default function Inbox() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { logInteraction, updateOutcome } = useAgentMemory();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [runningJob, setRunningJob] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');

  const fetchAlerts = async () => {
    if (!user) return;
    setLoading(true);

    const { data, error } = await supabase
      .from('alerts')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setAlerts(data as Alert[]);
    }
    setLoading(false);
  };

  const runAgentJobs = async () => {
    if (!user || runningJob) return;
    setRunningJob(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('run-agent-jobs', {
        body: { user_id: user.id, job_type: 'full_scan' },
      });

      if (error) throw error;

      toast.success(`Agent scan complete! Created ${data.events_created} events and ${data.alerts_created} alerts.`);
      fetchAlerts();
    } catch (error) {
      console.error('Agent job error:', error);
      toast.error('Failed to run agent scan');
    } finally {
      setRunningJob(false);
    }
  };

  // Generate synthetic alerts from existing data
  const generateSyntheticAlerts = async () => {
    if (!user) return;

    const { data: invoices } = await supabase
      .from('invoices')
      .select('*')
      .eq('user_id', user.id)
      .eq('status', 'pending')
      .gt('risk_score', 50);

    const { data: transactions } = await supabase
      .from('bank_transactions')
      .select('*')
      .eq('user_id', user.id)
      .eq('status', 'unmatched')
      .limit(10);

    const syntheticAlerts: Alert[] = [];

    invoices?.forEach((invoice) => {
      syntheticAlerts.push({
        id: `inv-${invoice.id}`,
        alert_type: invoice.flag?.includes('Duplicate') ? 'duplicate_invoice' : 'anomaly',
        title: invoice.flag || `High-risk invoice from ${invoice.vendor}`,
        description: `Invoice #${invoice.invoice_no} for ₹${invoice.amount.toLocaleString()} flagged with ${invoice.risk_score}% risk score`,
        severity: invoice.risk_score >= 80 ? 'critical' : invoice.risk_score >= 60 ? 'high' : 'medium',
        status: 'pending',
        money_impact: invoice.amount,
        evidence: { vendor: invoice.vendor, risk_score: invoice.risk_score, flag: invoice.flag },
        action_required: true,
        related_entity_type: 'invoice',
        related_entity_id: invoice.id,
        created_at: invoice.created_at,
      });
    });

    if (transactions && transactions.length > 0) {
      const totalUnmatched = transactions.reduce((acc, t) => acc + Math.abs(t.amount), 0);
      syntheticAlerts.push({
        id: 'unmatched-txns',
        alert_type: 'unmatched_transaction',
        title: `${transactions.length} unmatched transactions`,
        description: `Review and match ${transactions.length} bank transactions totaling ₹${totalUnmatched.toLocaleString()}`,
        severity: transactions.length > 20 ? 'high' : 'medium',
        status: 'pending',
        money_impact: totalUnmatched,
        evidence: { count: transactions.length, sample: transactions.slice(0, 3) },
        action_required: false,
        related_entity_type: 'transaction',
        related_entity_id: null,
        created_at: new Date().toISOString(),
      });
    }

    const realAlerts = alerts.filter(a => !a.id.startsWith('inv-') && a.id !== 'unmatched-txns');
    setAlerts([...syntheticAlerts, ...realAlerts]);
  };

  useEffect(() => {
    fetchAlerts();
  }, [user]);

  useEffect(() => {
    if (!loading && alerts.length === 0) {
      generateSyntheticAlerts();
    }
  }, [loading, user]);

  useEffect(() => {
    const channel = supabase
      .channel('alerts-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'alerts' },
        () => fetchAlerts()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleResolveAlert = async (alertId: string, newStatus: AlertStatus) => {
    // Log the interaction for agent learning
    const interactionId = await logInteraction({
      interaction_type: 'action',
      input: { alertId, action: newStatus },
      context: { page: 'inbox', alertType: alerts.find(a => a.id === alertId)?.alert_type },
      outcome: 'pending'
    });

    if (alertId.startsWith('inv-')) {
      const invoiceId = alertId.replace('inv-', '');
      await supabase
        .from('invoices')
        .update({ status: newStatus === 'approved' ? 'approved' : 'blocked' })
        .eq('id', invoiceId);
      toast.success(`Invoice ${newStatus}`);
    } else if (alertId === 'unmatched-txns') {
      navigate('/exceptions');
      return;
    } else {
      await supabase
        .from('alerts')
        .update({ status: newStatus, resolved_at: new Date().toISOString(), resolved_by: user?.id })
        .eq('id', alertId);
    }

    // Update outcome for learning
    if (interactionId) {
      await updateOutcome(interactionId, 'success', newStatus === 'approved' ? 'positive' : 'negative');
    }

    setAlerts((prev) =>
      prev.map((a) => (a.id === alertId ? { ...a, status: newStatus } : a))
    );
  };

  const handleViewEvidence = (alert: Alert) => {
    setSelectedAlert(alert);
    setEvidenceOpen(true);
  };

  const pendingAlerts = alerts.filter((a) => a.status === 'pending');
  const resolvedAlerts = alerts.filter((a) => a.status !== 'pending');

  const buildEvidenceItems = (alert: Alert) => {
    const items: { type: 'match' | 'mismatch' | 'info' | 'warning'; label: string; value: string; detail?: string }[] = [];
    
    if (alert.evidence) {
      if (alert.evidence.risk_score) {
        items.push({
          type: alert.evidence.risk_score >= 70 ? 'mismatch' : 'warning',
          label: 'Risk Score',
          value: `${alert.evidence.risk_score}%`,
          detail: 'Based on pattern matching and historical data',
        });
      }
      if (alert.evidence.flag) {
        items.push({
          type: 'warning',
          label: 'Flag Reason',
          value: alert.evidence.flag,
        });
      }
      if (alert.evidence.vendor) {
        items.push({
          type: 'info',
          label: 'Vendor',
          value: alert.evidence.vendor,
        });
      }
      if (alert.evidence.count) {
        items.push({
          type: 'info',
          label: 'Items Count',
          value: alert.evidence.count.toString(),
        });
      }
    }

    return items;
  };

  return (
    <div className="min-h-full bg-background">
      <SEO title="Finance Inbox - Sentinel" description="Your daily financial action items" />

      <div className="p-4 md:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-3xl bg-gradient-primary shadow-glow">
              <InboxIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">Finance Inbox</h1>
              <p className="text-sm text-muted-foreground">Things you should look at today</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {pendingAlerts.length > 0 && (
              <Badge variant="destructive" className="font-bold rounded-2xl px-4 py-1">
                {pendingAlerts.length} pending
              </Badge>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={runAgentJobs} 
              disabled={runningJob}
              className="rounded-2xl"
            >
              {runningJob ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run Scan
            </Button>
            <Button variant="ghost" size="sm" onClick={fetchAlerts} className="rounded-2xl">
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Inbox Area */}
          <div className="lg:col-span-2 space-y-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 rounded-3xl p-1.5 h-auto">
                <TabsTrigger value="pending" className="flex items-center gap-2 rounded-2xl py-3">
                  <Bell className="h-4 w-4" />
                  Pending
                  {pendingAlerts.length > 0 && (
                    <Badge variant="secondary" className="ml-1 rounded-xl">{pendingAlerts.length}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="resolved" className="flex items-center gap-2 rounded-2xl py-3">
                  <CheckCircle className="h-4 w-4" />
                  Resolved
                </TabsTrigger>
              </TabsList>

              <TabsContent value="pending" className="mt-6 space-y-4">
                <AnimatePresence mode="popLayout">
                  {loading ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex items-center justify-center py-16"
                    >
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </motion.div>
                  ) : pendingAlerts.length === 0 ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                    >
                      <Card className="p-12 text-center rounded-3xl border-dashed">
                        <Sparkles className="h-16 w-16 text-success mx-auto mb-6" />
                        <h3 className="text-xl font-semibold text-foreground mb-3">All Caught Up!</h3>
                        <p className="text-muted-foreground max-w-sm mx-auto">
                          Sentinel is monitoring your finances 24/7. We'll notify you when something needs attention.
                        </p>
                        <Button 
                          variant="outline" 
                          className="mt-6 rounded-2xl"
                          onClick={runAgentJobs}
                          disabled={runningJob}
                        >
                          {runningJob ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                          Run Agent Scan
                        </Button>
                      </Card>
                    </motion.div>
                  ) : (
                    pendingAlerts.map((alert, idx) => (
                      <motion.div
                        key={alert.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: idx * 0.05 }}
                      >
                        <ActionCard
                          id={alert.id}
                          type={alert.alert_type}
                          title={alert.title}
                          description={alert.description || undefined}
                          severity={alert.severity}
                          status={alert.status}
                          moneyImpact={alert.money_impact || undefined}
                          evidence={alert.evidence ? Object.keys(alert.evidence) : undefined}
                          actionRequired={alert.action_required}
                          createdAt={alert.created_at}
                          icon={alertTypeIcons[alert.alert_type]}
                          onApprove={alert.action_required ? () => handleResolveAlert(alert.id, 'approved') : undefined}
                          onReject={alert.action_required ? () => handleResolveAlert(alert.id, 'rejected') : undefined}
                          onDismiss={() => handleResolveAlert(alert.id, 'dismissed')}
                          onViewEvidence={() => handleViewEvidence(alert)}
                        />
                      </motion.div>
                    ))
                  )}
                </AnimatePresence>
              </TabsContent>

              <TabsContent value="resolved" className="mt-6 space-y-4">
                {resolvedAlerts.length === 0 ? (
                  <Card className="p-12 text-center rounded-3xl border-dashed">
                    <p className="text-muted-foreground">No resolved items yet</p>
                  </Card>
                ) : (
                  resolvedAlerts.slice(0, 20).map((alert, idx) => (
                    <motion.div
                      key={alert.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.03 }}
                    >
                      <ActionCard
                        id={alert.id}
                        type={alert.alert_type}
                        title={alert.title}
                        description={alert.description || undefined}
                        severity={alert.severity}
                        status={alert.status}
                        moneyImpact={alert.money_impact || undefined}
                        createdAt={alert.created_at}
                        icon={alertTypeIcons[alert.alert_type]}
                        onViewEvidence={() => handleViewEvidence(alert)}
                      />
                    </motion.div>
                  ))
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Agent Timeline Sidebar */}
          <div className="lg:col-span-1">
            <AgentTimeline className="rounded-3xl" />
          </div>
        </div>
      </div>

      {/* Evidence Drawer */}
      {selectedAlert && (
        <EvidenceDrawer
          open={evidenceOpen}
          onOpenChange={setEvidenceOpen}
          title={selectedAlert.title}
          description={selectedAlert.description || undefined}
          evidence={buildEvidenceItems(selectedAlert)}
          relatedData={selectedAlert.related_entity_type ? [
            {
              label: 'Related Item',
              items: [
                { key: 'Type', value: selectedAlert.related_entity_type },
                { key: 'ID', value: selectedAlert.related_entity_id || 'N/A' },
              ],
            },
          ] : undefined}
        />
      )}
    </div>
  );
}
